import { useEffect, useRef } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { useMediaQuery } from "react-responsive";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";

import Layout from "components/Layout";
import BoxLoadingSpinner from "components/other/BoxLoadingSpinner";
import PageTitle from "components/text/PageTitle";
import PostListBox from "components/posts/PostListBox";
import ButtonPrimaryIcon from "components/buttons/ButtonPrimaryIcon";
import ModalWrap from "components/other/ModalWrap";
import Backdrop from "components/other/Backdrop";
import ModalWindow from "components/other/ModalWindow";
import TutorialBox from "components/other/TutorialBox";
import ButtonPrimary from "components/buttons/ButtonPrimary";
import ButtonWhite from "components/buttons/ButtonWhite";
import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";
import RoundActionIcon from "components/buttons/RoundActionIcon";
import ContextMenu from "components/menu/ContextMenu";

import blockIcon from "assets/img/icons/icons8-unavailable.svg";
import plusIcon from "assets/img/icons/plus-sign.svg";
import plusWhiteIcon from "assets/img/icons/plus-sign-white.svg";
import leftIcon from "assets/img/icons/left-arrow.svg";
import rightIcon from "assets/img/icons/right-arrow.svg";
import leftWhiteIcon from "assets/img/icons/left-arrow-white.svg";
import rightWhiteIcon from "assets/img/icons/right-arrow-white.svg";
import filterIcon from "assets/img/icons/icons8-filter.svg";
import filterWhiteIcon from "assets/img/icons/icons8-filter-white.svg";

const Posts = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;

  margin: 0 0 50px;
  padding: 0 28px;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};

  @media (max-width: ${globalConstants.switchTablet}) {
    margin: 0;
    padding: 0;
  }
`;

const PostBox = styled.div`
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const NoResults = styled.div`
  width: 100%;
  height: 210px;
  margin-top: 75px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const NoResultsIcon = styled.div`
  width: 32px;
  min-width: 32px;
  height: 32px;
`;

const NoResultsText = styled.div`
  text-align: center;
`;

const NoResultButtonWrap = styled(Link)`
  margin-top: ${css.VERTICAL_BOX_MARGIN};

  text-decoration: none;
`;

const RemoveFilterButtonWrap = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN};
`;

const ConfirmDelete = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: auto;
  transform: translateX(-50%) translateY(-50%);

  width: 100%;
  max-width: 442px;
  padding: 0 10px;
  box-sizing: border-box;
  z-index: 200;

  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: ${globalConstants.switchMobile}) {
    width: 100%;
  }
`;

const ConfirmDeleteWrap = styled.div`
  margin-top: 60px;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ConfirmDeleteText = styled.div`
  line-height: 130%;
`;

const ConfirmDeleteButtons = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_XLARGE};

  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${css.BUTTONS_GAP};
`;

const ButtonWrap = styled(Link)`
  display: flex;

  text-decoration: none;
`;

const PostList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const Pagination = styled.div`
  width: 100%;
  margin-top: ${css.VERTICAL_BOX_MARGIN};

  display: flex;
  justify-content: center;
  gap: ${css.BUTTONS_GAP_SMALL};

  @media (max-width: ${globalConstants.switchTablet}) {
    margin-top: 6px;
    margin-bottom: ${css.VERTICAL_BOX_MARGIN};
  }
`;

const Header = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const HeaderIcons = styled.div`
  display: flex;
  align-items: center;
  gap: ${css.BUTTONS_GAP_SMALL};
`;

const MenuWrap = styled.div`
  position: relative;
`;

const MenuIconBox = styled.div`
  position: relative;
`;

const ActiveStatusDot = styled.div`
  position: absolute;
  top: 0;
  left: 25px;

  z-index: 100;
`;

const FilterMenu = styled.div`
  position: absolute;
  top: 42px;
  right: 0;
  z-index: 10;

  transition: 0.2s;

  opacity: ${(props) => (props.opened ? 100 : 0)};
  pointer-events: ${(props) => (props.opened ? "auto" : "none")};
`;

const FilterOption = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const OptionText = styled.div``;

const StatusDot = styled.div`
  width: 12px;
  min-width: 12px;
  height: 12px;

  border-radius: 50%;

  background: ${(props) => props.color};
`;

export default ({
  loading,
  data,
  onSwitchClick,
  switchLoading,
  onEditClick,
  onDeleteClick,
  confirmDelete,
  closeDeleteModal,
  onConfirmDelete,
  loadingDelete,
  onDuplicateClick,
  tutorialBox,
  closeBox,
  page,
  paginate,
  limit,
  onFilterMenuClick,
  closeFilterMenu,
  filterMenuOpened,
  onFilterSelected,
  statusFilter,
}) => {
  // set page Title
  useEffect(() => {
    document.title = "Posts | Scalelup";
  }, []);

  const tabletScreen = useMediaQuery({
    query: `(max-width: ${globalConstants.switchTablet})`,
  });

  const menuIconRef = useRef();

  const posts = data ? data.posts.posts : [];
  const numOfResults = data ? data.posts.numOfResults : 0;

  const list = posts.map((post) => {
    return (
      <PostListBox
        key={post.id}
        post={post}
        onSwitchClick={onSwitchClick}
        switchLoading={switchLoading}
        onEditClick={onEditClick}
        onDeleteClick={onDeleteClick}
        loadingDelete={loadingDelete}
        onDuplicateClick={onDuplicateClick}
        page={page}
      />
    );
  });

  let postToConfirm;

  if (data && confirmDelete) {
    postToConfirm = posts.find((post) => post.id === confirmDelete);
  }

  let showPagination = false;

  if (numOfResults > limit) showPagination = true;

  const maxPage = Math.ceil(numOfResults / limit);

  // prepare Status filter
  let filterColor;

  switch (statusFilter) {
    case "active":
      filterColor = css.BRAND_GREEN;
      break;
    case "paused":
      filterColor = css.BRAND_BLUSH;
      break;
    case "inReview":
      filterColor = css.BRAND_INDIGO;
      break;
  }

  console.log("statusFilter", statusFilter);

  return (
    <Layout>
      <Posts>
        {tutorialBox && (
          <TutorialBox
            title="Generate Coins by Viewing Posts"
            text="By regularly viewing user posts, you ensure continuous exposure for your offer, which will guarantee a steady stream of new leads."
            closeBox={closeBox}
            buttonTo="/discover"
            buttonText="Discover"
          />
        )}
        <Header>
          <PageTitle marginBottom="0">Posts</PageTitle>
          <HeaderIcons>
            <MenuWrap>
              <MenuIconBox ref={menuIconRef}>
                <RoundActionIcon
                  icon={filterMenuOpened ? filterWhiteIcon : filterIcon}
                  iconAlt="Chat menu"
                  onClick={onFilterMenuClick}
                  iconSize="16px"
                />
                {statusFilter && (
                  <ActiveStatusDot>
                    <StatusDot color={filterColor} />
                  </ActiveStatusDot>
                )}
              </MenuIconBox>
              <FilterMenu opened={filterMenuOpened ? 1 : 0}>
                <ContextMenu
                  items={[
                    {
                      icon: null,
                      label: (
                        <FilterOption>
                          <OptionText>All</OptionText>
                        </FilterOption>
                      ),
                      onClick: () => onFilterSelected(null),
                    },
                    {
                      icon: null,
                      label: (
                        <FilterOption>
                          <OptionText>Active</OptionText>
                          <StatusDot color={css.BRAND_GREEN} />
                        </FilterOption>
                      ),
                      onClick: () => onFilterSelected("active"),
                    },
                    {
                      icon: null,
                      label: (
                        <FilterOption>
                          <OptionText>Paused</OptionText>
                          <StatusDot color={css.BRAND_BLUSH} />
                        </FilterOption>
                      ),
                      onClick: () => onFilterSelected("paused"),
                    },
                    {
                      icon: null,
                      label: (
                        <FilterOption>
                          <OptionText>In Review</OptionText>
                          <StatusDot color={css.BRAND_INDIGO} />
                        </FilterOption>
                      ),
                      onClick: () => onFilterSelected("inReview"),
                    },
                  ]}
                  menuIconRef={menuIconRef}
                  closeMenu={closeFilterMenu}
                  alignItems="flex-end"
                />
              </FilterMenu>
            </MenuWrap>
          </HeaderIcons>
        </Header>
        {loading && (
          <PostBox>
            <BoxLoadingSpinner height="285px" />
          </PostBox>
        )}
        <PostList>
          {!loading && list}
          {showPagination && (
            <Pagination>
              {page > 1 && (
                <ButtonRoundIcon
                  icon={leftIcon}
                  hoverIcon={leftWhiteIcon}
                  background={css.BRAND_YELLOW}
                  hoverBackground={css.BRAND_GREEN}
                  iconSize="16px"
                  buttonSize="32px"
                  onClick={() => paginate("previous")}
                />
              )}
              {page < maxPage && (
                <ButtonRoundIcon
                  icon={rightIcon}
                  hoverIcon={rightWhiteIcon}
                  background={css.BRAND_YELLOW}
                  hoverBackground={css.BRAND_GREEN}
                  iconSize="16px"
                  buttonSize="32px"
                  onClick={() => paginate("next")}
                />
              )}
            </Pagination>
          )}
        </PostList>
        {!loading && list.length === 0 && (
          <PostBox>
            <NoResults>
              <NoResultsIcon>
                <img src={blockIcon} style={{ width: "100%" }} />
              </NoResultsIcon>
              {!statusFilter && (
                <NoResultsText>You don't have any posts.</NoResultsText>
              )}
              {statusFilter && (
                <NoResultsText>
                  No posts were found for the selected filter.
                </NoResultsText>
              )}
              {!statusFilter && (
                <NoResultButtonWrap to="/modify-post">
                  <ButtonPrimaryIcon icon={plusIcon} iconHover={plusWhiteIcon}>
                    Create Post
                  </ButtonPrimaryIcon>
                </NoResultButtonWrap>
              )}
              {statusFilter && (
                <RemoveFilterButtonWrap>
                  <ButtonPrimary onClick={() => onFilterSelected(null)}>
                    Show All Posts
                  </ButtonPrimary>
                </RemoveFilterButtonWrap>
              )}
            </NoResults>
          </PostBox>
        )}
        {!loading && list.length > 0 && tabletScreen && (
          <ButtonWrap to="/modify-post">
            <ButtonPrimaryIcon icon={plusIcon} iconHover={plusWhiteIcon}>
              Create Post
            </ButtonPrimaryIcon>
          </ButtonWrap>
        )}
        {postToConfirm && (
          <ModalWrap>
            <Backdrop
              onClick={closeDeleteModal}
              background="rgba(0, 0, 0, 0.7)"
            />
            <ConfirmDelete>
              <ModalWindow
                title="Delete Post"
                handleModalClose={closeDeleteModal}
                fullScreenHeight={true}
              >
                <ConfirmDeleteWrap>
                  <ConfirmDeleteText>
                    Do you really want to delete the post "{postToConfirm.name}
                    "?
                  </ConfirmDeleteText>
                  <ConfirmDeleteButtons>
                    <ButtonWhite onClick={closeDeleteModal}>Cancel</ButtonWhite>
                    <ButtonPrimary onClick={onConfirmDelete}>
                      Delete
                    </ButtonPrimary>
                  </ConfirmDeleteButtons>
                </ConfirmDeleteWrap>
              </ModalWindow>
            </ConfirmDelete>
          </ModalWrap>
        )}
      </Posts>
    </Layout>
  );
};
